import { useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Dialog, Field, Icon, Money } from './ui.jsx';
import { diffImport, FIELDS, monthsFromImport, LOG_TYPES } from '../lib/sheetImport.js';
import { applyImport } from '../lib/actions.js';
import { buildModel } from '../lib/model.js';
import { monthLabel } from '../lib/calendar.js';

// Shows exactly what a spreadsheet would change before anything is changed
export default function ImportDialog({ parsed, fileName, onClose }) {
  const { data, model, commit, today, sheets, me } = useApp();
  const metaPerson = data.people.find((p) => p.id === parsed.meta?.personId);
  const [pid, setPid] = useState(metaPerson?.id || me || data.people[0]?.id);
  const person = data.people.find((p) => p.id === pid);
  // The table layout is turned into months for this person (salaries go to the month their payday belongs to)
  const asMonths = useMemo(() => ({ ...parsed, rows: monthsFromImport(parsed, person) }), [parsed, person]);
  const diff = useMemo(() => diffImport(data, model, pid, asMonths), [data, model, pid, asMonths]);
  const linked = sheets?.[pid];
  const apply = () => {
    const res = commit(`Import ${diff.changes.length} values for ${person.name} from ${fileName}`, (s) => applyImport(s, buildModel(s, { today, sheets }), pid, diff.changes, { by: me || 'spreadsheet' }), { undoToast: true });
    if (!res.error) onClose();
  };
  const show = (field, v, items) => (v === null || v === undefined ? <span className="muted">—</span> : field === 'note' ? <span>{String(v)}</span> : (
    <span><Money value={v} />{items && items.length > 0 && <span className="small muted" style={{ display: 'block', whiteSpace: 'normal' }}>{items.map((x) => x.label).filter(Boolean).join(', ')}</span>}</span>
  ));
  return (
    <Dialog
      wide
      title={`Import ${fileName}`}
      onClose={onClose}
      footer={(
        <>
          <span className="small muted">{diff.unchanged ? `${diff.unchanged} values already match` : ''}</span>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={!diff.changes.length} onClick={apply}>{diff.changes.length ? `Apply ${diff.changes.length} changes` : 'Nothing to change'}</button>
        </>
      )}
    >
      <div className="grid2">
        <Field label="Whose figures are these?" hint={metaPerson ? `The file says it's ${metaPerson.name}'s` : parsed.meta?.personName ? `The file says “${parsed.meta.personName}”` : 'Pick the person'}>
          <select className="input" value={pid} onChange={(e) => setPid(e.target.value)}>
            {data.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <div className="small muted" style={{ alignSelf: 'end' }}>
          {parsed.kind === 'log'
            ? `Read ${parsed.entries.length} rows from “${parsed.sheet}”: ${['salary', 'income', 'expense'].map((t) => `${parsed.entries.filter((e) => e.type === t).length} ${LOG_TYPES[t].toLowerCase()}`).join(', ')}`
            : `Read from sheet “${parsed.sheet}”: ${Object.entries(parsed.columns || {}).filter(([k]) => k !== 'month').map(([k, v]) => `${v} → ${FIELDS[k]}`).join(' · ')}`}
        </div>
      </div>
      {metaPerson && metaPerson.id !== pid && <div className="banner warn"><Icon name="alert" className="icon" /><div>This template was made for {metaPerson.name}, not {person?.name}.</div></div>}
      {linked && <div className="banner warn"><Icon name="alert" className="icon" /><div>{person?.name} has a linked spreadsheet: its values win over what's imported here for the same cells.</div></div>}
      {[...(parsed.warnings || []), ...diff.warnings].length > 0 && (
        <div className="banner warn" role="status">
          <Icon name="alert" className="icon" />
          <div className="stack tight">
            <b>Some rows were skipped</b>
            <ul style={{ margin: 0, paddingLeft: 18 }}>{[...(parsed.warnings || []), ...diff.warnings].slice(0, 12).map((w, i) => <li key={i} className="small">{w}</li>)}</ul>
          </div>
        </div>
      )}
      {!diff.changes.length ? <p className="dim">Nothing new: the app already has these values.</p> : (
        <>
          <p className="dim">Paydays that get a take-home amount are marked as confirmed. Everything can be undone.</p>
          <table className="plain">
            <thead><tr><th>Month</th><th className="left">What</th><th>Now</th><th>After import</th></tr></thead>
            <tbody>
              {diff.changes.map((c) => (
                <tr key={`${c.month}${c.field}`}>
                  <td>{monthLabel(c.month)}</td>
                  <td className="left">{FIELDS[c.field]}</td>
                  <td>{show(c.field, c.from)}</td>
                  <td style={{ fontWeight: 600 }}>{show(c.field, c.to, c.items)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Dialog>
  );
}
